import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { down } from 'styled-breakpoints';

import { CART_STEP } from '../steps';

import Icon from './icon';

import {
  calculatePriceAfterDiscount,
  getDiscountDetails,
} from '../selectors/repair-price';

import deleteSelectedDevice from '../actions/delete-selected-device';
import addAnotherDevice, {
  abandonAddAnotherDevice,
} from '../actions/add-another-device';
import trackButton from '../actions/track-button';

import DeviceBreakdown from './device-breakdown';

import { buildHandleEnterKeyPress } from '../utils/accessibility';
import dollars from '../utils/dollars';

const getCreditCardImage = type => {
  try {
    const imageSource = require(`./credit-card-icons/${type}.png`);

    return <IconImage alt="Credit Card Icon" src={imageSource} />;
  } catch (e) {
    return (
      <IconImage
        alt="Credit Card Icon"
        src={require('./credit-card-icons/default.png')}
      />
    );
  }
};

class Cart extends Component {
  onAddAnotherDevice = () => {
    const { disableAddNewDevice } = this.props;

    this.props.trackButton({
      disabled: disableAddNewDevice,
      name: 'add_another_device',
      step: CART_STEP,
    });

    this.props.addAnotherDevice();
  };

  onDeleteDevice = ({ reduxId }) => () => {
    this.props.trackButton({ name: 'delete_device', step: CART_STEP });
    this.props.deleteSelectedDevice(reduxId);
  };

  onAbandonDevice = () => {
    this.props.trackButton({ name: 'abandon_device', step: CART_STEP });

    this.props.abandonAddAnotherDevice();
  };

  formattedWindow() {
    const { start, end, preciseTimes, selectedTimes } = this.props;
    const [selectedTime] = selectedTimes.filter(time => time.preferred);
    const time = this.props.disableAltTimes ? { start, end } : selectedTime;

    if (time?.start) {
      if (preciseTimes) {
        return time.start.format('LT z');
      } else {
        return `${time.start.format('LT z')} - ${time.end.format('LT z')}`;
      }
    }
  }

  displayServiceRequestTime() {
    const { isRutgers, start, end } = this.props;

    if (isRutgers) {
      return 'Be sure to deliver your device by 11:00am';
    } else {
      return Boolean(start) && Boolean(end) && this.formattedWindow();
    }
  }

  render() {
    const {
      addressComponents,
      archive,
      cardType,
      device,
      disableAddNewDevice,
      discountDetails,
      displayAddress,
      disableAltTimes,
      groupPays,
      issueOptions,
      lastFour,
      prices,
      overflow,
      newDeviceText,
      selectDeviceText,
      finalCostText,
      selectedColor,
      selectedDeviceType,
      selectedIssues,
      selectedStore,
      serviceOption,
      specialInstructions,
      total,
      totalAfterDiscount,
      selectedTimes,
      start,
    } = this.props;

    const regex = /\s*,\s*/;
    const adjustedAddress = displayAddress?.replace(
      regex,
      `, ${addressComponents.unitNumber}, `
    );
    const [selectedTime] = selectedTimes.filter(time => time.preferred);
    const selectedDay = disableAltTimes ? start : selectedTime?.start;

    return (
      <Container shouldOverflow={overflow}>
        {archive.map((d, i) => (
          <DeviceBreakdown
            device={d.model}
            deviceColor={d.color}
            deviceType={d.deviceType}
            groupPays={groupPays}
            issueOptions={issueOptions}
            issues={d.issues}
            key={`${d.model.id}-${i}`}
            onDelete={this.onDeleteDevice(d)}
            prices={d.prices}
            selectDeviceText={selectDeviceText}
          />
        ))}

        <DeviceBreakdown
          device={device}
          deviceColor={selectedColor}
          deviceType={selectedDeviceType}
          disableDelete={archive.length < 1}
          groupPays={groupPays}
          issueOptions={issueOptions}
          issues={selectedIssues}
          onDelete={this.onAbandonDevice}
          prices={prices}
        />

        {!disableAddNewDevice &&
          this.props.dynamicContent.allowAddNewDevice && (
            <AddNewDeviceButton
              onClick={this.onAddAnotherDevice}
              text={newDeviceText}
            />
          )}

        {displayAddress ? (
          <SectionContainer showBorder={!groupPays}>
            <IconImage as="span">
              <Icon name="office" />
            </IconImage>

            <div className="text-container">
              {serviceOption === 'IN_STORE' && selectedStore ? (
                <>
                  <div className="text">{selectedStore.name}</div>
                  <div className="text">{selectedStore.displayAddress}</div>
                </>
              ) : null}

              {serviceOption === 'ON_SITE' ? (
                <div className="text">
                  {addressComponents.unitNumber
                    ? adjustedAddress
                    : displayAddress}
                </div>
              ) : null}

              <div className="text">
                <em>{specialInstructions}</em>
              </div>

              <div className="text">
                {selectedDay?.format('dddd, LL') || ''}
              </div>

              <div className="text">{this.displayServiceRequestTime()}</div>
            </div>
          </SectionContainer>
        ) : null}

        {!groupPays && (
          <CreditCardContainer>
            {Boolean(lastFour) && Boolean(cardType) && (
              <SectionContainer showBorder={true}>
                {getCreditCardImage(cardType)}

                <div className="text">
                  <span className="cc-text">{'•••• •••• •••• '}</span>
                  {lastFour}
                </div>
              </SectionContainer>
            )}

            <div className="total-container">
              {Boolean(discountDetails.value) && (
                <div className="price-container">
                  <div className="price-label">Subtotal</div>
                  <div className="price-value">{dollars(total / 100)}</div>
                </div>
              )}

              {Boolean(discountDetails.value) && (
                <div className="price-container">
                  <div className="price-label">{discountDetails.label}</div>
                  <div className="price-value">
                    {`-${dollars(discountDetails.value / 100 || 0)}`}
                  </div>
                </div>
              )}

              <div className="price-container">
                <div className="price-label">{finalCostText}</div>
                <div className="price-value">
                  {dollars(totalAfterDiscount / 100 || 0)}
                </div>
              </div>
            </div>
          </CreditCardContainer>
        )}
      </Container>
    );
  }
}

Cart.propTypes = {
  overflow: PropTypes.bool,
  cardType: PropTypes.string,
  device: PropTypes.object,
  disableAddNewDevice: PropTypes.bool,
  discountDetails: PropTypes.object.isRequired,
  displayAddress: PropTypes.string,
  end: PropTypes.instanceOf(moment),
  groupPays: PropTypes.bool,
  issueOptions: PropTypes.array,
  lastFour: PropTypes.string,
  loading: PropTypes.bool,
  prices: PropTypes.object,
  repairIsLoading: PropTypes.bool,
  selectedColor: PropTypes.string,
  selectedDay: PropTypes.instanceOf(moment),
  selectedIssues: PropTypes.array,
  specialInstructions: PropTypes.string,
  start: PropTypes.instanceOf(moment),
  total: PropTypes.number,
  totalAfterDiscount: PropTypes.number,
};

Cart.defaultProps = {
  overflow: true,
  cardType: '',
  device: {},
  disableAddNewDevice: false,
  displayAddress: '',
  groupPays: false,
  issueOptions: [],
  lastFour: '',
  loading: false,
  prices: {},
  repairIsLoading: false,
  selectedColor: '',
  selectedIssues: [],
  specialInstructions: '',
  total: 0,
  totalAfterDiscount: 0,
};

const AddNewDeviceButton = ({ onClick, text }) => (
  <AddNewDeviceContainer
    onClick={onClick}
    onKeyPress={buildHandleEnterKeyPress(onClick)}
    tabIndex={0}
  >
    <img
      alt="Add New Device"
      className="add-image"
      src={require('./add.png')}
    />

    <p>{text}</p>
  </AddNewDeviceContainer>
);

const Container = styled.div`
  min-width: 300px;
  width: 100%;
  max-height: 100%;

  ${props => {
    if (props.shouldOverflow) {
      return `
        overflow: auto;
        max-height: 400px;
      `;
    }
  }}
  ${down('sm')} {
    min-width: 218px;
  }
`;

const SectionContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 1em;

  ${props => {
    if (props.showBorder) {
      return `
        border-bottom: 1px solid ${props.theme.border}`;
    }
  }}

  .text-container {
    width: 100%;
  }

  .text {
    color: ${props => props.theme.subText};
    font-size: 80%;
    margin: 0.5em 0;

    .cc-text {
      font-size: 1.5em;
    }
  }
`;

const IconImage = styled.img`
  font-size: 24px;
  height: auto;
  margin: 0 1em 0 0;
  width: 45px;
`;

const CreditCardContainer = styled.span`
  .total-container {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 1em;

    .price-container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin: 0.25em 0;
      width: 100%;

      .price-label {
        color: ${props => props.theme.text};
      }
      .price-value {
        color: ${props => props.theme.text};
        text-align: right;
      }
    }
  }
`;

const AddNewDeviceContainer = styled.div`
  align-items: center;
  color: ${props => props.theme.subText};
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  padding: 0 1em;
  border-bottom: ${props => `1px solid ${props.theme.border}`};

  .add-image {
    height: 25px;
    width: 25px;
    margin-right: 12px;
  }
`;

const mapState = ({
  group: { groupPays, slug, disableAltTimes },
  location: {
    displayAddress,
    specialInstructions,
    addressComponents,
    selectedStore,
    serviceOption,
  },
  pageText: {
    cart: { newDeviceText, selectDeviceText },
  },
  payment,
  payment: { cardType, error, lastFour, loading, number },
  repair: { repairIsLoading },
  scheduledWindow: { end, preciseTimes, selectedDay, start, selectedTimes },
  selectedDevices,
  selectedDevices: {
    archive,
    color,
    deviceType,
    issues: selectedIssues,
    issueOptions,
    model,
    prices,
    prices: { total },
  },
  dynamicContent,
}) => ({
  addressComponents,
  archive,
  cardType,
  device: model,
  discountDetails: getDiscountDetails({ payment, selectedDevices }),
  displayAddress,
  disableAltTimes,
  dynamicContent,
  end,
  groupPays,
  isRutgers: slug.includes('rutgers'),
  issueOptions,
  lastFour,
  loading,
  preciseTimes,
  prices,
  repairIsLoading,
  newDeviceText,
  selectDeviceText,
  selectedColor: color,
  selectedDay,
  selectedDeviceType: deviceType,
  selectedIssues,
  selectedStore,
  selectedTimes,
  serviceOption,
  slug,
  start,
  specialInstructions,
  total,
  totalAfterDiscount: calculatePriceAfterDiscount({ payment, selectedDevices }),
});

const mapActions = {
  abandonAddAnotherDevice,
  addAnotherDevice,
  deleteSelectedDevice,
  trackButton,
};

export default connect(mapState, mapActions)(Cart);
